// ==========================================
// FILE: \src\screens\AddWineScreen.js
// ==========================================

import React, { useState, useRef } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, 
  ScrollView, TextInput, Image, KeyboardAvoidingView, Platform, Modal
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient';
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  Camera, UploadCloud, CheckCircle, MinusCircle, PlusCircle, 
  ArrowLeft, AlertCircle, Sparkles, Pencil, Info, Gem, Unlock,
  LayoutGrid, PackageOpen 
} from 'lucide-react-native';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

export default function AddWineScreen({ navigation }) {
  // Estraiamo la funzione helper 't' dal context per gestire la UI multilingua
  const { addWineLocal, consumeEnergy, addEnergy, userProfile, t } = useWineContext();
  const isPremium = userProfile?.isPremium || false;

  const [imageUri, setImageUri] = useState(null);
  const [newWineForm, setNewWineForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // --- LOCKS ANTI-SPAM ---
  const [isExtracting, setIsExtracting] = useState(false);
  const [isFetchingSommelier, setIsFetchingSommelier] = useState(false);
  const [isRecalculatingWindow, setIsRecalculatingWindow] = useState(false);
  const [isUnlockingValue, setIsUnlockingValue] = useState(false); 

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [permission, requestPermission] = useCameraPermissions();
  const cameraRef = useRef(null);

  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null });
  const [editingField, setEditingField] = useState(null); 
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const [showVintagePrompt, setShowVintagePrompt] = useState(false);
  const [tempVintageInput, setTempVintageInput] = useState('');

  // --- STATI PER LA MONETIZZAZIONE ---
  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const showAppAlert = (title, message, type = 'info', onConfirm = null) => setCustomAlert({ visible: true, title, message, type, onConfirm });
  const closeAppAlert = () => setCustomAlert(prev => ({ ...prev, visible: false }));

  const openInternalCamera = async () => {
    if (!permission?.granted) {
      const { granted } = await requestPermission();
      if (!granted) return showAppAlert(t('addWine.camera.permissionDeniedTitle'), t('addWine.camera.permissionDeniedMsg'), "error");
    }
    setIsCameraOpen(true);
  };

  const takePhoto = async () => {
    if (cameraRef.current) {
      try {
        const photo = await cameraRef.current.takePictureAsync({ quality: 1 });
        setIsCameraOpen(false); 
        processAndExtract(photo.uri); 
      } catch (error) {
        setIsCameraOpen(false);
        showAppAlert(t('addWine.camera.shootErrorTitle'), t('addWine.camera.shootErrorMsg'), "error");
      }
    }
  };

  const pickFromGallery = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], allowsEditing: false, quality: 1 });
      if (!result.canceled) processAndExtract(result.assets[0].uri);
    } catch (error) { 
      showAppAlert(t('global.error'), t('addWine.camera.galleryErrorMsg'), "error"); 
    }
  };

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('addWine.alerts.adCompleteTitle'), t('addWine.alerts.adCompleteMsg'), "success");
  };

  const processAndExtract = async (uri) => {
    if (isExtracting) return; 
    setIsExtracting(true); 

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsExtracting(false); 
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsExtracting(false); 
      return; 
    }

    setNewWineForm(null);

    try {
      const manipResult = await ImageManipulator.manipulateAsync(
        uri, [{ resize: { width: 800 } }], { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true }
      );

      setImageUri(manipResult.uri);
      const base64Data = manipResult.base64;

      // Traduciamo esplicitamente la lingua dell'utente per istruire Gemini
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Analizza questa immagine. 
REGOLE FONDAMENTALI:
1. RICONOSCIMENTO: Verifica se l'immagine contiene un'etichetta o una bottiglia di vino. Se NON è vino imposta "isWine" a false.
2. DEDUZIONE & LINGUA: Deduci i dati mancanti usando la tua conoscenza enologica. Tutti i campi testuali generati ("name", "description", "pairingTags", "vitigno", "affinamento", "regione") DEVONO essere scritti rigorosamente in lingua ${targetLanguageName}.
3. ANNATA: Estrai l'anno. Se non è presente o è uno Champagne/Spumante senza annata, scrivi "S.A.".
4. FORCHETTA DI CONSUMO: Calcola il "drinkingWindow" (es. '2025-2032') partendo dall'annata estratta e dal potenziale di invecchiamento di quel vitigno.
5. VALORE: Stima il prezzo di mercato in Euro per singola bottiglia. Usa SOLO UN NUMERO INTERO (es. 25). Se impossibile, metti 0.

La struttura JSON DEVE essere ESATTAMENTE questa:
{
  "isWine": true,
  "name": "Nome del vino e cantina",
  "annata": "2019 o S.A.",
  "estimatedValue": 25,
  "type": "Rosso",
  "description": "Breve descrizione",
  "pairingTags": "Suggerimenti cibi separati da virgola",
  "vitigno": "Uve esatte",
  "gradazione": "Es. 13.5%",
  "temperatura": "Es. 16-18°C",
  "affinamento": "Metodo di affinamento",
  "regione": "Regione esatta",
  "drinkingWindow": "Es. '2025-2032'",
  "isReadyToDrink": true
}`;

      const extractedData = await callGemini(promptText, true, base64Data);

      if (extractedData.isWine === false) throw new Error("NOT_A_WINE");

      const isReadyBool = typeof extractedData.isReadyToDrink === 'boolean' ? extractedData.isReadyToDrink : extractedData.isReadyToDrink === 'true';
      const annataEstratta = extractedData.annata || 'S.A.';

      const finalDataObj = {
        ...extractedData,
        annata: annataEstratta,
        isReadyToDrink: isReadyBool,
        estimatedValue: parseInt(extractedData.estimatedValue) || 0,
        valueUnlocked: false, 
        image: `data:image/jpeg;base64,${base64Data}`, 
        quantity: 1, rating: 0, deepDetails: null
      };

      setNewWineForm(finalDataObj);
      setIsExtracting(false);

      if (annataEstratta.toUpperCase().includes('S.A.')) {
        setTempVintageInput('');
        setTimeout(() => setShowVintagePrompt(true), 500); 
      }

    } catch (error) {
      if (!isPremium) {
        await addEnergy(1);
      }

      if (error.message === "NOT_A_WINE") {
        showAppAlert(t('addWine.alerts.invalidImgTitle'), t('addWine.alerts.invalidImgMsg'), "error");
        setImageUri(null); setNewWineForm(null); setIsExtracting(false); return; 
      }
      
      showAppAlert(t('addWine.alerts.technicalErrorTitle'), t('addWine.alerts.technicalErrorMsg'), "error");
      setNewWineForm({
        name: "", annata: "S.A.", estimatedValue: 0, valueUnlocked: false, type: "Rosso", description: "", pairingTags: "", vitigno: "", 
        gradazione: "", temperatura: "", affinamento: "", regione: "", drinkingWindow: "", isReadyToDrink: true, 
        quantity: 1, rating: 0, image: imageUri, deepDetails: null
      });
      setIsExtracting(false); 
    } 
  };

  const handleUnlockValue = async () => {
    if (isUnlockingValue) return; 
    setIsUnlockingValue(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsUnlockingValue(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsUnlockingValue(false);
      return;
    }

    setTimeout(() => {
      setNewWineForm(prev => ({ ...prev, valueUnlocked: true }));
      setIsUnlockingValue(false);
    }, 2000);
  };

  const recalculateDrinkingWindow = async (vintageToUse) => {
    if (!newWineForm) return;
    if (isRecalculatingWindow) return; 
    
    setIsRecalculatingWindow(true);

    try {
      const promptText = `Sei un Master Sommelier. Ho questo vino: "${newWineForm.name}" (Annata: ${vintageToUse}, Tipo: ${newWineForm.type}, Regione: ${newWineForm.regione || 'Sconosciuta'}, Vitigno: ${newWineForm.vitigno || 'Sconosciuto'}).
      Calcola la forchetta di consumo ideale (drinkingWindow) basandoti sull'annata indicata.
      Rispondi SOLO ed ESCLUSIVAMENTE con questo formato JSON esatto: {"drinkingWindow": "2025-2032"}`;

      const extractedData = await callGemini(promptText, true);

      if (extractedData.drinkingWindow) {
        setNewWineForm(prev => ({ ...prev, drinkingWindow: extractedData.drinkingWindow }));
      }
    } catch (error) {
      console.log("Errore ricalcolo silenzioso forchetta:", error);
    } finally {
      setIsRecalculatingWindow(false);
    }
  };

  const handleVintagePromptSave = (finalVintage) => {
    setShowVintagePrompt(false);
    setNewWineForm(prev => ({ ...prev, annata: finalVintage }));
    
    if (finalVintage !== 'S.A.') {
      recalculateDrinkingWindow(finalVintage);
    }
  };

  const fetchSommelierDeepDetails = async () => {
    if (!newWineForm?.name) return;
    if (isFetchingSommelier) return; 

    setIsFetchingSommelier(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsFetchingSommelier(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsFetchingSommelier(false);
      return;
    }

    try {
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Agisci come un Master Sommelier. Scrivi un approfondimento testuale affascinante e dettagliato (massimo 3 paragrafi) sul vino "${newWineForm.name}" (Annata: ${newWineForm.annata}, Tipologia: ${newWineForm.type}, Regione: ${newWineForm.regione}, Vitigno: ${newWineForm.vitigno}). Rispondi rigorosamente in lingua ${targetLanguageName}. Includi storia della cantina o del territorio, particolarità organolettiche e curiosità. Non usare formattazione complessa.`;
      
      const generatedText = await callGemini(promptText, false);
      
      setNewWineForm(prev => ({ ...prev, deepDetails: generatedText.trim() }));
      showAppAlert(t('global.success'), t('addWine.alerts.saveSuccessMsg'), "success");
    } catch (error) {
      if (!isPremium) {
        await addEnergy(1);
      }
      showAppAlert(t('addWine.camera.permissionDeniedTitle'), t('addWine.alerts.technicalErrorMsg'), "error");
    } finally {
      setIsFetchingSommelier(false);
    }
  };

  const saveNewWine = async (shouldPlace = false) => {
    if (!newWineForm.name) return showAppAlert(t('addWine.alerts.nameRequiredTitle'), t('addWine.alerts.nameRequiredMsg'), "error");
    setIsSaving(true);
    try {
      const wineData = {
        ...newWineForm, 
        status: 'presente',
        // Registriamo permanentemente la lingua corrente in cui i testi sono stati memorizzati nel DB
        generatedLanguage: userProfile?.language || 'it',
        pairingTags: Array.isArray(newWineForm.pairingTags) ? newWineForm.pairingTags : newWineForm.pairingTags.split(',').map(tag => tag.trim()),
        createdAt: Date.now(),
        technicalSheet: {
          vitigno: newWineForm.vitigno, gradazione: newWineForm.gradazione, temperatura: newWineForm.temperatura, 
          affinamento: newWineForm.affinamento, regione: newWineForm.regione
        }
      };
      
      delete wineData.isWine; delete wineData.vitigno; delete wineData.gradazione; delete wineData.temperatura; delete wineData.affinamento; delete wineData.regione; 
      delete wineData.location; 
      
      const newId = await addWineLocal(wineData);
      
      if (shouldPlace) {
        navigation.navigate('Caveau', { autoPlaceId: newId });
      } else {
        showAppAlert(t('addWine.alerts.saveSuccessTitle'), t('addWine.alerts.saveSuccessMsg'), "success", () => {
          closeAppAlert(); navigation.navigate('Home');
        });
      }
    } catch (error) {
      showAppAlert(t('global.error'), t('addWine.alerts.saveCloudErrorMsg'), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (key, label) => setEditingField({ key, label, value: String(newWineForm[key] || '') });
  
  const saveEditedField = () => {
    setNewWineForm(prev => ({ ...prev, [editingField.key]: editingField.value }));
    if (editingField.key === 'annata' && editingField.value !== 'S.A.') {
      recalculateDrinkingWindow(editingField.value);
    }
    setEditingField(null);
  };

  const EditableRow = ({ label, fieldKey, isRecalculating }) => (
    <View style={styles.dataRow}>
      <View style={{flex: 1, paddingRight: 10}}>
        <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 2}}>
          <Text style={styles.dataLabel}>{label}</Text>
          {isRecalculating && <ActivityIndicator size="small" color="#e11d48" style={{marginLeft: 8}} />}
        </View>
        <Text style={[styles.dataValue, (fieldKey === 'name' && {fontSize: 18, fontWeight: 'bold'})]}>
          {newWineForm[fieldKey] || 'N/D'}
        </Text>
      </View>
      <TouchableOpacity onPress={() => openEditModal(fieldKey, label)} style={styles.editIconBtn}>
        <Pencil size={18} color="#94a3b8" />
      </TouchableOpacity>
    </View>
  );

  const TypeButton = ({ type }) => {
    const isSelected = newWineForm?.type === type;
    const typeLabelKey = type.toLowerCase() === 'bollicine' ? 'bollicine' : type.toLowerCase() === 'rosato' ? 'rosato' : type.toLowerCase() === 'dolce' ? 'dolce' : type.toLowerCase() === 'bianco' ? 'bianco' : 'rosso';
    return (
      <TouchableOpacity style={[styles.typeButton, isSelected && styles.typeButtonSelected]} onPress={() => setNewWineForm({...newWineForm, type})}>
        <Text style={[styles.typeButtonText, isSelected && styles.typeButtonTextSelected]}>
          {t(`addWine.form.types.${typeLabelKey}`)}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.mainWrapper}>
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        
        <View style={styles.topRedBanner}>
           <EnergyHeader />
        </View>

        <View style={styles.pageHeaderCushion}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
              <ArrowLeft color="#0f172a" size={24} />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>{t('addWine.title')}</Text>
          </View>
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          
          {!isExtracting && !newWineForm && (
            isCameraOpen ? (
              <View style={styles.cameraSection}>
                <View style={styles.cameraEmbeddedContainer}>
                  <CameraView style={StyleSheet.absoluteFillObject} facing="back" ref={cameraRef}>
                    <View style={styles.cameraOverlayEmbedded}>
                      <Text style={styles.cameraInstructions}>{t('addWine.camera.instructions')}</Text>
                      <View style={styles.focusFrameEmbedded}>
                        <View style={[styles.corner, styles.topLeft]} /><View style={[styles.corner, styles.topRight]} />
                        <View style={[styles.corner, styles.bottomLeft]} /><View style={[styles.corner, styles.bottomRight]} />
                      </View>
                    </View>
                  </CameraView>
                </View>
                <View style={styles.cameraActionButtons}>
                  <TouchableOpacity style={styles.cancelButton} onPress={() => setIsCameraOpen(false)}><Text style={styles.cancelButtonText}>{t('global.cancel')}</Text></TouchableOpacity>
                  <TouchableOpacity style={styles.actionButtonPrimary} onPress={takePhoto}><Camera color="#fff" size={20} style={{marginRight: 8}} /><Text style={styles.saveButtonText}>{t('addWine.camera.take')}</Text></TouchableOpacity>
                </View>
              </View>
            ) : (
              <View style={styles.uploadSection}>
                <TouchableOpacity style={styles.uploadCard} onPress={openInternalCamera}><Camera color="#e11d48" size={48} /><Text style={styles.uploadTitle}>{t('addWine.camera.shootPhoto')}</Text><Text style={styles.uploadSub}>{t('addWine.camera.smartCam')}</Text></TouchableOpacity>
                <TouchableOpacity style={styles.uploadCard} onPress={pickFromGallery}><UploadCloud color="#e11d48" size={48} /><Text style={styles.uploadTitle}>{t('addWine.camera.fromGallery')}</Text><Text style={styles.uploadSub}>{t('addWine.camera.uploadImg')}</Text></TouchableOpacity>
              </View>
            )
          )}

          {isExtracting && (
            <View style={styles.loadingSection}>
              <ActivityIndicator size="large" color="#e11d48" />
              <Text style={styles.loadingTitle}>{t('addWine.loading.title')}</Text>
              <Text style={styles.loadingSub}>{t('addWine.loading.sub')}</Text>
            </View>
          )}

          {newWineForm && !isExtracting && (
            <View style={styles.formContainer}>
              <View style={styles.successBanner}>
                <CheckCircle color="#16a34a" size={20} />
                <Text style={styles.successText}>{t('addWine.form.successBanner')}</Text>
              </View>

              <View style={styles.topRow}>
                {imageUri && <Image source={{ uri: imageUri }} style={styles.previewImage} resizeMode="cover" />}
                <View style={styles.quantityContainer}>
                  <Text style={styles.inputLabelCentered}>{t('addWine.form.bottles')}</Text>
                  <View style={styles.quantityControls}>
                    <TouchableOpacity onPress={() => setNewWineForm({...newWineForm, quantity: Math.max(1, newWineForm.quantity - 1)})}>
                      <MinusCircle color="#e11d48" size={32} />
                    </TouchableOpacity>
                    <Text style={styles.quantityText}>{newWineForm.quantity}</Text>
                    <TouchableOpacity onPress={() => setNewWineForm({...newWineForm, quantity: newWineForm.quantity + 1})}>
                      <PlusCircle color="#e11d48" size={32} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <Text style={styles.sectionLabel}>{t('addWine.form.type')}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeSelector}>
                <TypeButton type="Rosso" />
                <TypeButton type="Bianco" />
                <TypeButton type="Bollicine" />
                <TypeButton type="Rosato" />
                <TypeButton type="Dolce" />
              </ScrollView>

              <View style={styles.detailsCard}>
                <EditableRow label={t('addWine.form.labels.name')} fieldKey="name" />
                <EditableRow label={t('addWine.form.labels.annata')} fieldKey="annata" />
                <EditableRow label={t('addWine.form.labels.vitigno')} fieldKey="vitigno" />
                <EditableRow label={t('addWine.form.labels.regione')} fieldKey="regione" />
                <EditableRow label={t('addWine.form.labels.gradazione')} fieldKey="gradazione" />
                <EditableRow label={t('addWine.form.labels.affinamento')} fieldKey="affinamento" />
                <EditableRow label={t('addWine.form.labels.temperatura')} fieldKey="temperatura" />
                <EditableRow 
                  label={isRecalculatingWindow ? t('addWine.form.labels.recalcWindow') : t('addWine.form.labels.drinkingWindow')} 
                  fieldKey="drinkingWindow" 
                  isRecalculating={isRecalculatingWindow}
                />
                <EditableRow label={t('addWine.form.labels.pairingTags')} fieldKey="pairingTags" />
              </View>

              <View style={styles.aiActionGroup}>
                <View style={styles.valueEstimationBox}>
                  {!newWineForm.valueUnlocked ? (
                    <TouchableOpacity style={styles.unlockValueBtn} onPress={handleUnlockValue} disabled={isUnlockingValue}>
                      {isUnlockingValue ? (
                        <ActivityIndicator color="#0ea5e9" size="small" style={{marginRight: 8}} />
                      ) : (
                        <Unlock color="#0ea5e9" size={18} style={{marginRight: 8}} />
                      )}
                      <Text style={styles.unlockValueText}>
                        {isUnlockingValue ? t('addWine.aiActions.marketAnalysis') : t('addWine.aiActions.estimateValue')}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.unlockedValueContainer}>
                      <View style={{flexDirection: 'row', alignItems: 'center'}}>
                        <Gem color="#0ea5e9" size={20} style={{marginRight: 8}} />
                        <Text style={styles.unlockedValueText}>{t('addWine.aiActions.estimation')}: {newWineForm.estimatedValue > 0 ? `${newWineForm.estimatedValue} €` : 'N/D'}</Text>
                      </View>
                      <TouchableOpacity onPress={() => setShowDisclaimer(true)} style={styles.infoBtn}>
                        <Info color="#94a3b8" size={18} />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                <View style={styles.sommelierSection}>
                  {newWineForm.deepDetails ? (
                    <View style={styles.sommelierCard}>
                      <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 8}}>
                        <Sparkles color="#e11d48" size={18} style={{marginRight: 6}} />
                        <Text style={{fontWeight: 'bold', color: '#0f172a'}}>{t('addWine.aiActions.sommelierNote')}</Text>
                      </View>
                      <Text style={{color: '#475569', lineHeight: 20}}>{newWineForm.deepDetails}</Text>
                    </View>
                  ) : (
                    <TouchableOpacity style={styles.generateSommelierBtn} onPress={fetchSommelierDeepDetails} disabled={isFetchingSommelier}>
                      {isFetchingSommelier ? (
                        <ActivityIndicator color="#e11d48" size="small" />
                      ) : (
                        <>
                          <Sparkles color="#e11d48" size={18} style={{marginRight: 8}} />
                          <Text style={styles.generateSommelierText}>{t('addWine.aiActions.generateReview')}</Text>
                        </>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              <View style={styles.footerButtonsVertical}>
                <TouchableOpacity 
                  style={styles.saveAndPlaceBtn} 
                  onPress={() => saveNewWine(true)} 
                  disabled={isSaving}
                >
                  {isSaving ? <ActivityIndicator color="#fff" /> : (
                    <>
                      <LayoutGrid color="#fff" size={22} style={{marginRight: 10}} />
                      <Text style={styles.saveButtonText}>{t('addWine.buttons.saveAndPlace')}</Text>
                    </>
                  )}
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.saveSimpleBtn} 
                  onPress={() => saveNewWine(false)} 
                  disabled={isSaving}
                >
                  <PackageOpen color="#475569" size={20} style={{marginRight: 10}} />
                  <Text style={styles.saveSimpleText}>{t('addWine.buttons.saveOnly')}</Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.cancelLink} onPress={() => setNewWineForm(null)}>
                  <Text style={styles.cancelLinkText}>{t('addWine.buttons.cancelOp')}</Text>
                </TouchableOpacity>
              </View>

            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* --- MODAL INSERIMENTO ANNATA --- */}
      <Modal transparent={true} visible={showVintagePrompt} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              <View style={{backgroundColor: '#fff7ed', padding: 8, borderRadius: 12}}>
                <AlertCircle color="#ea580c" size={24} />
              </View>
              <Text style={styles.modalTitle}>{t('addWine.buttons.vintageNotFound')}</Text>
            </View>
            
            <Text style={styles.modalMessage}>{t('addWine.buttons.vintageModalMsg')}</Text>
            
            <TextInput
              style={styles.vintageInput}
              placeholder={t('addWine.form.placeholders.vintageInput')}
              placeholderTextColor="#cbd5e1"
              keyboardType="number-pad"
              maxLength={4}
              value={tempVintageInput}
              onChangeText={setTempVintageInput}
            />

            <View style={[styles.modalButtons, {marginTop: 20}]}>
              <TouchableOpacity 
                style={[styles.modalCancelBtn, {flex: 1}]} 
                onPress={() => handleVintagePromptSave('S.A.')}
              >
                <Text style={[styles.modalCancelText, {fontSize: 14, textAlign: 'center'}]}>{t('addWine.buttons.unknownVintage')}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.modalConfirmBtn, {flex: 1, backgroundColor: '#ea580c'}]} 
                onPress={() => {
                  const finalYear = tempVintageInput.trim().length === 4 ? tempVintageInput.trim() : 'S.A.';
                  handleVintagePromptSave(finalYear);
                }}
              >
                <Text style={styles.modalConfirmText}>{t('addWine.buttons.saveYear')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL MODIFICA CAMPO */}
      <Modal transparent={true} visible={!!editingField} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={styles.modalTitle}>{t('addWine.buttons.editTitle')} {editingField?.label}</Text>
            <TextInput 
              style={[styles.input, { marginTop: 16, marginBottom: 24 }]}
              value={editingField?.value}
              onChangeText={t => setEditingField({...editingField, value: t})}
              multiline={editingField?.key === 'pairingTags'}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setEditingField(null)}>
                <Text style={styles.modalCancelText}>{t('global.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalConfirmBtn, {backgroundColor: '#e11d48', minWidth: 80}]} onPress={saveEditedField}>
                <Text style={styles.modalConfirmText}>{t('global.ok')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL DISCLAIMER */}
      <Modal transparent={true} visible={showDisclaimer} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}><Info color="#0ea5e9" size={28} /><Text style={styles.modalTitle}>{t('addWine.buttons.valueInfoTitle')}</Text></View>
            <Text style={styles.modalMessage}>{t('addWine.buttons.valueInfoMsg')}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalConfirmBtn, {backgroundColor: '#0f172a'}]} onPress={() => setShowDisclaimer(false)}><Text style={styles.modalConfirmText}>{t('login.buttons.dismiss')}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL SISTEMA */}
      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : customAlert.type === 'success' ? <CheckCircle color="#10b981" size={28} /> : <Sparkles color="#3b82f6" size={28} />}
              <Text style={styles.modalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalMessage}>{customAlert.message}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalConfirmBtn, customAlert.type === 'error' ? { backgroundColor: '#e11d48' } : customAlert.type === 'success' ? { backgroundColor: '#10b981' } : { backgroundColor: '#0f172a' }]} onPress={() => { if (customAlert.onConfirm) customAlert.onConfirm(); else closeAppAlert(); }}><Text style={styles.modalConfirmText}>{t('global.ok')}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <OutOfEnergyModal 
        visible={showOutOfEnergy}
        onClose={() => setShowOutOfEnergy(false)}
        onWatchAd={() => {
          setShowOutOfEnergy(false);
          setShowAdModal(true); 
        }}
        onSubscribe={() => {
          setShowOutOfEnergy(false);
          navigation.navigate('Paywall');
        }}
      />

      <RewardedAdModal 
        visible={showAdModal}
        onClose={() => setShowAdModal(false)}
        onReward={handleAdComplete}
      />

    </View>
  );
}

const styles = StyleSheet.create({
  mainWrapper: { flex: 1, backgroundColor: '#f8fafc' }, 
  container: { flex: 1 },
  topRedBanner: { backgroundColor: '#e11d48', paddingBottom: 16, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, elevation: 5, boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)', zIndex: 50 },
  pageHeaderCushion: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'white', marginHorizontal: 16, marginTop: 16, padding: 16, borderRadius: 20, elevation: 3, boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)', zIndex: 10 },
  headerLeft: { flexDirection: 'row', alignItems: 'center' },
  backButton: { marginRight: 10, padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a' },
  scrollContent: { padding: 20, paddingBottom: 40 },
  uploadSection: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 },
  uploadCard: { flex: 1, backgroundColor: '#fff', padding: 24, borderRadius: 20, alignItems: 'center', marginHorizontal: 8, borderWidth: 2, borderColor: '#ffe4e6', borderStyle: 'dashed' },
  uploadTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginTop: 12, textAlign: 'center' },
  uploadSub: { fontSize: 12, color: '#64748b', marginTop: 4, textAlign: 'center' },
  loadingSection: { alignItems: 'center', justifyContent: 'center', marginTop: 100 },
  loadingTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginTop: 20 },
  loadingSub: { fontSize: 14, color: '#64748b', marginTop: 8 },
  formContainer: { backgroundColor: 'transparent' },
  successBanner: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0fdf4', padding: 12, borderRadius: 12, marginBottom: 20, borderWidth: 1, borderColor: '#bbf7d0' },
  successText: { color: '#166534', fontWeight: 'bold', marginLeft: 8 },
  topRow: { flexDirection: 'row', marginBottom: 20 },
  previewImage: { width: 100, height: 140, borderRadius: 12, backgroundColor: '#f1f5f9' },
  quantityContainer: { flex: 1, marginLeft: 20, backgroundColor: 'white', borderRadius: 16, padding: 16, justifyContent: 'center', boxShadow: '0px 2px 5px rgba(0,0,0,0.05)' },
  quantityControls: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 },
  quantityText: { fontSize: 28, fontWeight: 'bold', color: '#0f172a' },
  inputLabelCentered: { fontSize: 14, fontWeight: 'bold', color: '#64748b', textAlign: 'center' },
  sectionLabel: { fontSize: 14, fontWeight: 'bold', color: '#475569', marginBottom: 8, marginTop: 8, marginLeft: 4 },
  typeSelector: { flexDirection: 'row', marginBottom: 24 },
  typeButton: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 20, backgroundColor: 'white', marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  typeButtonSelected: { backgroundColor: '#e11d48', borderColor: '#e11d48' },
  typeButtonText: { color: '#64748b', fontWeight: '600' }, typeButtonTextSelected: { color: 'white' },
  detailsCard: { backgroundColor: 'white', borderRadius: 20, padding: 16, boxShadow: '0px 2px 5px rgba(0,0,0,0.05)', marginBottom: 20 },
  dataRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  dataLabel: { fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: 2 },
  dataValue: { fontSize: 16, color: '#0f172a', fontWeight: '500' },
  editIconBtn: { padding: 8 },
  aiActionGroup: { backgroundColor: 'white', padding: 16, borderRadius: 20, boxShadow: '0px 2px 5px rgba(0,0,0,0.05)', marginBottom: 20 },
  valueEstimationBox: { backgroundColor: '#f0f9ff', padding: 16, borderRadius: 16, marginBottom: 16, borderWidth: 1, borderColor: '#bae6fd' },
  unlockValueBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 4 },
  unlockValueText: { color: '#0ea5e9', fontWeight: 'bold', fontSize: 16 },
  unlockedValueContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  unlockedValueText: { fontSize: 18, fontWeight: 'bold', color: '#0369a1' },
  infoBtn: { padding: 4 },
  generateSommelierBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff1f2', padding: 16, borderRadius: 16, borderWidth: 1, borderColor: '#ffe4e6', borderStyle: 'dashed' },
  generateSommelierText: { color: '#e11d48', fontWeight: 'bold', fontSize: 16 },
  sommelierCard: { backgroundColor: '#fff1f2', padding: 20, borderRadius: 16, borderWidth: 1, borderColor: '#ffe4e6' },
  input: { backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 12, padding: 14, fontSize: 16, color: '#0f172a' },
  footerButtonsVertical: { gap: 12, marginTop: 10 },
  saveAndPlaceBtn: { flexDirection: 'row', backgroundColor: '#e11d48', paddingVertical: 18, borderRadius: 16, alignItems: 'center', justifyContent: 'center', elevation: 4 },
  saveSimpleBtn: { flexDirection: 'row', backgroundColor: 'white', paddingVertical: 16, borderRadius: 16, alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#e2e8f0' },
  
  // Allineamento anti-sfasamento: flex-1 e wrap per evitare troncamenti nelle lingue lunghe
  saveSimpleText: { color: '#475569', fontWeight: 'bold', fontSize: 14, textAlign: 'center', paddingHorizontal: 10, flex: 1 },
  saveButtonText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  cancelLink: { alignItems: 'center', marginTop: 8 },
  cancelLinkText: { color: '#94a3b8', fontWeight: '600', fontSize: 16 },
  vintageInput: { backgroundColor: '#f8fafc', borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 12, padding: 16, fontSize: 24, color: '#0f172a', textAlign: 'center', fontWeight: 'bold', marginTop: 16 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0px 10px 25px rgba(0,0,0,0.2)', elevation: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12, flex: 1 },
  modalMessage: { fontSize: 15, color: '#475569', lineHeight: 22, marginBottom: 24 },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  modalCancelBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center', backgroundColor: '#f1f5f9' },
  modalCancelText: { color: '#475569', fontWeight: 'bold', fontSize: 16 },
  modalConfirmBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  cameraSection: { marginTop: 10 },
  cameraEmbeddedContainer: { height: 420, borderRadius: 24, overflow: 'hidden', backgroundColor: '#0f172a', position: 'relative' },
  cameraOverlayEmbedded: { flex: 1, backgroundColor: 'rgba(0,0,0,0.3)', justifyContent: 'center', alignItems: 'center' },
  cameraInstructions: { position: 'absolute', top: 30, color: 'white', fontSize: 16, fontWeight: 'bold', textShadow: '1px 1px 5px rgba(0,0,0,0.8)' },
  focusFrameEmbedded: { width: 220, height: 300, borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)', position: 'relative' },
  corner: { position: 'absolute', width: 30, height: 30, borderColor: '#e11d48' },
  topLeft: { top: -2, left: -2, borderTopWidth: 4, borderLeftWidth: 4 }, topRight: { top: -2, right: -2, borderTopWidth: 4, borderRightWidth: 4 },
  bottomLeft: { bottom: -2, left: -2, borderBottomWidth: 4, borderLeftWidth: 4 }, bottomRight: { bottom: -2, right: -2, borderBottomWidth: 4, borderRightWidth: 4 },
  cameraActionButtons: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 20 },
  cancelButton: { paddingVertical: 14, paddingHorizontal: 20, borderRadius: 12, backgroundColor: '#f1f5f9', marginRight: 10, justifyContent: 'center' },
  cancelButtonText: { color: '#475569', fontWeight: 'bold' },
  actionButtonPrimary: { flexDirection: 'row', backgroundColor: '#e11d48', paddingVertical: 14, paddingHorizontal: 24, borderRadius: 12, minWidth: 150, alignItems: 'center', justifyContent: 'center' }
});